// What the RFP email will say "Not specified" for.
//
// Step 5 (RFP Parameters) is what the broker asks carriers to quote — those
// values are printed straight into the carrier email (ConsolidatedRFPEmail.tsx).
// Nothing is required, deliberately: brokers send open RFPs ("quote your best
// PPO") as well as spec'd ones. But sending a blank one means every carrier
// quotes to its own defaults, and the proposals that come back don't compare.
// So we don't block — we say what's missing, at the moment before sending.

export type ParameterGap = { coverageType: string; label: string; missing: string[] };

const isBlank = (v: unknown) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0) || v === 0;

function dentalGaps(p: any): string[] {
  const missing: string[] = [];
  if (isBlank(p?.coverageTypes)) missing.push("Coverage type");
  if (isBlank(p?.networkType)) missing.push("Network type");
  // "Custom Amount" parks the real figure in customMaximum and leaves the
  // dropdown at 0, so either one counts as specified.
  if (isBlank(p?.maximumBenefit) && isBlank(p?.customMaximum)) missing.push("Maximum benefit");
  if (isBlank(p?.perioEndoCoverage)) missing.push("Perio/endo coverage");
  if (isBlank(p?.orthodonticCoverage)) missing.push("Orthodontic coverage");
  return missing;
}

function visionGaps(p: any): string[] {
  const missing: string[] = [];
  if (isBlank(p?.coverageTypes)) missing.push("Coverage type");
  if (isBlank(p?.materialsCopay)) missing.push("Materials copay");
  if (isBlank(p?.benefitsFrequency)) missing.push("Benefits frequency");
  if (isBlank(p?.allowanceAmount)) missing.push("Frame allowance");
  return missing;
}

function lifeGaps(p: any): string[] {
  const missing: string[] = [];
  if (isBlank(p?.coverageTypes)) missing.push("Coverage type");
  // A row exists by default but is empty until a flat amount or salary multiple
  // is entered — an empty row asks the carrier for nothing.
  const amounts = Array.isArray(p?.coverageAmounts) ? p.coverageAmounts : [];
  if (!amounts.some((a: any) => !isBlank(a?.flatAmount) || !isBlank(a?.salaryMultiple))) {
    missing.push("Coverage amount");
  }
  return missing;
}

function disabilityGaps(p: any): string[] {
  const missing: string[] = [];
  const types: string[] = p?.coverageTypes ?? [];
  if (isBlank(types)) return ["Coverage type"];
  const check = (params: any, prefix: string) => {
    if (isBlank(params?.eliminationPeriod)) missing.push(`${prefix} elimination period`);
    if (isBlank(params?.benefitDuration)) missing.push(`${prefix} benefit duration`);
    if (isBlank(params?.benefitPercentage)) missing.push(`${prefix} benefit percentage`);
  };
  if (types.includes("std")) check(p?.stdParams, "STD");
  if (types.includes("ltd")) check(p?.ltdParams, "LTD");
  return missing;
}

function voluntaryGaps(p: any): string[] {
  return isBlank(p?.selectedBenefits) ? ["Benefits to quote"] : [];
}

// Four of the form's dozen fields: the ones that decide whether proposals can
// be compared at all. isBlank treats 0 as blank, so the money checks read the
// raw value — a $0 deductible is a real plan design, not a blank.
function healthGaps(p: any): string[] {
  const missing: string[] = [];
  const unanswered = (v: unknown) => v == null || v === "";
  if (isBlank(p?.networkTypes)) missing.push("Network type");
  if (isBlank(p?.fundingTypes)) missing.push("Funding type");
  if (unanswered(p?.deductibleIndividual)) missing.push("Deductible");
  if (unanswered(p?.outOfPocketIndividual)) missing.push("Out-of-pocket max");
  return missing;
}

const CHECKS: Record<string, { label: string; fn: (p: any) => string[] }> = {
  health: { label: "Medical", fn: healthGaps },
  dental: { label: "Dental", fn: dentalGaps },
  vision: { label: "Vision", fn: visionGaps },
  life: { label: "Life & AD&D", fn: lifeGaps },
  disability: { label: "Disability", fn: disabilityGaps },
  voluntary: { label: "Voluntary", fn: voluntaryGaps },
};

/** Gaps for the coverage types this RFP actually covers. Empty = fully specified. */
export function findParameterGaps(
  coverageTypes: string[],
  rfpParameters: Record<string, any>,
): ParameterGap[] {
  const gaps: ParameterGap[] = [];
  for (const type of coverageTypes) {
    const check = CHECKS[type];
    if (!check) continue;
    const missing = check.fn(rfpParameters?.[type]);
    if (missing.length) gaps.push({ coverageType: type, label: check.label, missing });
  }
  return gaps;
}

export function countGaps(gaps: ParameterGap[]): number {
  return gaps.reduce((total, g) => total + g.missing.length, 0);
}
